/*
 * JuvaBun shade swatches: repaint white / gray photo backgrounds in the bun-photo cream (#faf3e3).
 * Markup: <img data-jb-swfix="fit|bg"> or <span data-jb-swfix="bg"><img></span> (snippets/jb-swatch-img.liquid,
 * sections/jb-pdp-v3.liquid). Many variant photos (ponytails, toppers, a few bun shades) were shot on white, so the
 * round swatch looked like an empty white circle next to the cream bun photos.
 *  - bg:  only when the photo background is NOT already cream: background pixels (flood fill from the edges) become
 *         cream, framing unchanged (Messy Bun page).
 *  - fit: same, then crop to the hair so it fills the circle (any background).
 * Same-origin images only (juvabun.com/cdn/shop); if the canvas is tainted or anything fails, the photo stays as is.
 * Vanilla, deferred, runs once per image (a copy of a fixed image is skipped), guards against loading twice.
 * JBSwatchFix.scan(root) wires swatches added later (bundle quick view copies the card swatches).
 */
(() => {
  if (window.JBSwatchFix) return;
  const CREAM = [250, 243, 227];
  const OUT = 120; /* 'fit' output px (shown at 40-56px, so 2x screens stay sharp); 'bg' keeps the photo size (max 240) */
  const near = (d, i, c, tol) => Math.abs(d[i] - c[0]) <= tol && Math.abs(d[i + 1] - c[1]) <= tol && Math.abs(d[i + 2] - c[2]) <= tol;

  const done = new WeakSet();
  const wired = new WeakSet();

  function process(img, mode) {
    if (done.has(img) || String(img.currentSrc || img.src).startsWith('data:')) return;
    done.add(img);
    const W = img.naturalWidth;
    const H = img.naturalHeight;
    if (!W || !H) return;
    const s = Math.min(1, 240 / Math.max(W, H));
    const w = Math.max(1, Math.round(W * s));
    const h = Math.max(1, Math.round(H * s));
    const c = document.createElement('canvas');
    c.width = w;
    c.height = h;
    const x = c.getContext('2d', { willReadFrequently: true });
    let px;
    try {
      x.drawImage(img, 0, 0, w, h);
      px = x.getImageData(0, 0, w, h);
    } catch (e) {
      return; /* cross-origin (tainted canvas): leave the photo alone */
    }
    const d = px.data;
    /* Background colour = average of the 4 corners (they must agree, else this is not a plain backdrop) */
    const corners = [0, w - 1, (h - 1) * w, h * w - 1].map((p) => p * 4);
    const bg = [0, 1, 2].map((k) => Math.round(corners.reduce((a, i) => a + d[i + k], 0) / 4));
    if (!corners.every((i) => near(d, i, bg, 24))) return;
    if (d[corners[0] + 3] < 200) return; /* transparent PNG: the swatch's own background already shows */
    const isCream = near([...bg, 255], 0, CREAM, 8);
    if (mode === 'bg' && isCream) return;

    /* Flood fill the backdrop from every edge pixel (tolerance 20 per channel) */
    const TOL = 20;
    const mask = new Uint8Array(w * h);
    const stack = [];
    for (let i = 0; i < w; i++) { stack.push(i, (h - 1) * w + i); }
    for (let j = 0; j < h; j++) { stack.push(j * w, j * w + w - 1); }
    while (stack.length) {
      const p = stack.pop();
      if (mask[p]) continue;
      if (!near(d, p * 4, bg, TOL)) continue;
      mask[p] = 1;
      const px0 = p % w;
      if (px0 > 0) stack.push(p - 1);
      if (px0 < w - 1) stack.push(p + 1);
      if (p >= w) stack.push(p - w);
      if (p < w * (h - 1)) stack.push(p + w);
    }
    /* Repaint the backdrop + find the hair's box */
    let x0 = w, y0 = h, x1 = -1, y1 = -1;
    for (let p = 0; p < w * h; p++) {
      const i = p * 4;
      if (mask[p]) {
        d[i] = CREAM[0]; d[i + 1] = CREAM[1]; d[i + 2] = CREAM[2]; d[i + 3] = 255;
      } else {
        const px1 = p % w;
        const py1 = (p - px1) / w;
        if (px1 < x0) x0 = px1;
        if (px1 > x1) x1 = px1;
        if (py1 < y0) y0 = py1;
        if (py1 > y1) y1 = py1;
      }
    }
    if (x1 < 0) return; /* nothing but backdrop */
    x.putImageData(px, 0, 0);

    /* 'bg': same photo, same framing, new backdrop. 'fit': square crop around the hair (outside the photo = cream):
       a round bun fits whole, a long ponytail shows its middle section so the colour fills the circle */
    let o = c;
    if (mode === 'fit') {
      const bw = x1 - x0 + 1;
      const bh = y1 - y0 + 1;
      const side = Math.max(24, Math.min(Math.max(bw, bh), Math.min(bw, bh) * 1.15));
      const k = OUT / side;
      o = document.createElement('canvas');
      o.width = OUT;
      o.height = OUT;
      const ox = o.getContext('2d');
      ox.fillStyle = `rgb(${CREAM.join(',')})`;
      ox.fillRect(0, 0, OUT, OUT);
      ox.drawImage(c, -(x0 + bw / 2 - side / 2) * k, -(y0 + bh / 2 - side / 2) * k, w * k, h * k);
    }
    try {
      const url = o.toDataURL('image/jpeg', 0.92);
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      img.src = url;
      img.classList.add(mode === 'fit' ? 'is-swfit' : 'is-swbg');
    } catch (e) { /* ignore */ }
  }

  function wire(img, mode) {
    if (wired.has(img)) return;
    wired.add(img);
    const go = () => process(img, mode);
    if (img.complete && img.naturalWidth) go();
    else img.addEventListener('load', go, { once: true });
  }

  function scan(root) {
    (root || document).querySelectorAll('img[data-jb-swfix], [data-jb-swfix] > img').forEach((img) => {
      const holder = img.hasAttribute('data-jb-swfix') ? img : img.parentElement;
      wire(img, holder.getAttribute('data-jb-swfix') === 'bg' ? 'bg' : 'fit');
    });
  }

  window.JBSwatchFix = { scan };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan());
  else scan();
})();
